// src/ProductPage/ProductDetails.tsx
import React from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { toProductSlug } from "../config";

type BackendProduct = {
  id: string;
  name: string;
  slug?: string | null;
  customSchema?: string | null;
  altText?: string | null;
  productType: string;
  shortDescription?: string | null;
  longDescription?: string | null;
  price: number;
  galleryImages?: string[] | null;
  rotationImages?: string[] | null;
};

interface ProductDetailsProps {
  product: BackendProduct;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({ product }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const formatPrice = (value: number) => {
    const a =
      value > 0
        ? `${value.toLocaleString(undefined, { minimumFractionDigits: 0 })}`
        : ``;
    return a;
  };

  const subtitle = product.shortDescription || product.productType;
  const priceLabel = formatPrice(product.price);

  const fallbackDescription =
    "This piece is crafted with high-quality materials and careful workmanship, designed to be comfortable for everyday wear while still feeling special for important occasions.";
  const description = product.longDescription?.trim()
    ? product.longDescription
    : fallbackDescription;

  const whatsappNumber = process.env.REACT_APP_WHATSAPP_PHONE;

  const productUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/product/${product.slug}`
      : `/product/${product.slug}`;

  const whatsappMessage = encodeURIComponent(
    `Hi, I want to consult about this product:\n${product.name}\n${productUrl}`,
  );

  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 6 } }}>
      {/* Title + subtitle */}
      <Typography
        variant="h5"
        sx={{
          fontFamily: `"Bodoni Moda", serif`,
          fontWeight: 600,
          mb: 0.5,
        }}
      >
        {product.name}
      </Typography>
      {subtitle && (
        <Typography variant="subtitle1" sx={{ color: "grey.700", mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      <Typography variant="h6" sx={{ mb: 3 }}>
        {priceLabel}
      </Typography>
      <Divider sx={{ mb: 4 }} />

      {/* Desktop: description above buttons */}
      {!isMobile && (
        <Box sx={{ mb: 5 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: "grey.900",
              fontSize: "0.8rem",
              fontFamily: "Santral,Santral Fallback,Verdana,Sans-Serif",
            }}
          >
            {description}
          </Typography>

          {/* Bullets side by side */}
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  color: "grey.800",
                  fontFamily: "Santral,Santral Fallback,Verdana,Sans-Serif",
                  fontSize: "0.78rem",
                  textAlign: "center",
                }}
              >
                • Crafted with meticulous attention to detail
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  color: "grey.800",
                  fontFamily: "Santral,Santral Fallback,Verdana,Sans-Serif",
                  fontSize: "0.78rem",
                  textAlign: "center",
                }}
              >
                • Designed for everyday wear and special occasions
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  color: "grey.800",
                  fontFamily: "Santral,Santral Fallback,Verdana,Sans-Serif",
                  fontSize: "0.78rem",
                  textAlign: "center",
                }}
              >
                • A modern heirloom made to last
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Buttons */}
      <Box sx={{ mt: { xs: 4, md: 6 } }}>
        <Button
          fullWidth
          variant="contained"
          component="a"
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            bgcolor: "black",
            color: "white",
            borderRadius: 0,
            py: 1.5,
            fontWeight: 600,
            mb: 2,
            "&:hover": { bgcolor: "#333" },
          }}
        >
          Talk to an expert
        </Button>
      </Box>

      {/* Mobile: description below buttons */}
      {isMobile && (
        <Box sx={{ mt: 4 }}>
          <Typography
            sx={{
              mb: 2,
              color: "grey.900",
              fontSize: "0.95rem",
              lineHeight: 1.6,
              fontFamily: "Santral,Santral Fallback,Verdana,Sans-Serif",
            }}
          >
            {description}
          </Typography>
          <ul
            style={{
              marginLeft: "1rem",
              color: "#444",
              lineHeight: "1.6",
              fontFamily: "Santral,Santral Fallback,Verdana,Sans-Serif",
              fontSize: "0.9rem",
            }}
          >
            <li>Crafted with meticulous attention to detail</li>
            <li>Designed for everyday wear and special occasions</li>
            <li>A modern heirloom made to last</li>
          </ul>
        </Box>
      )}
    </Box>
  );
};

export default ProductDetails;
